// ============================================================================
// THE DRAFT, AS A HOOK — the Rule of Three, called in (blueprint/02).
//
// JRN-1a put the durable draft in the phone composer. JRN-1b added the
// online/visible resend and the honest chip. JRN-1c reused that composer for a
// past day rather than writing a second one. The desktop Today card carries its
// own copy of the same rules, and `routes/JournalDay.tsx` — which has said in a
// comment since the day it was written that the NEXT implementation should
// extract this — would have been the third.
//
// So this is the third, and it is a hook instead of a paste. What lives here is
// exactly the part that must not differ between composers:
//
//   · the words go to disk SYNCHRONOUSLY on every keystroke, before anything
//     that can be interrupted;
//   · the draft is forgotten only when the server confirms THOSE EXACT words
//     (the comparison lives in lib/journal-draft.ts, and the clearing happens in
//     useSaveJournalEntry's own onSuccess so an unmount flush still counts);
//   · a draft that differs from the server's text is put back at mount, ONCE,
//     so the textarea's `key` never changes again and the undo stack survives;
//   · nothing is resent unattended while the read is failing, or when sending
//     would replace an entry the draft never saw;
//   · the chip may only promise what is actually wired.
//
// WHAT IS DELIBERATELY NOT HERE: the debounce, the flush-on-leave, and every
// pixel. Each composer keeps its own — they differ (700ms vs 800ms, a card vs a
// page), and pulling them in would make this a shared editor rather than a
// shared rule. The undo contract stays where the textarea is.
// ============================================================================

import { useEffect, useRef, useState } from 'react'
import type { ISODate } from '@app/shared'

import {
  draftToRestore,
  draftWouldReplace,
  journalDraftStorage,
  readJournalDraft,
  writeJournalDraft,
} from './journal-draft'

/** The words a mount put back into the box, and when the phone caught them. */
export interface RestoredDraft {
  text: string
  typedAt: string
}

export interface JournalDraftState {
  /** Put back at mount, or null. Feeds `defaultValue` — never re-set afterwards. */
  restored: RestoredDraft | null
  /** There is a draft the server has not confirmed. The chip must not say "Saved". */
  unconfirmed: boolean
  /** The last draft write actually landed. False in a browser that refuses storage. */
  kept: boolean
  /** Something will send these words with no further tap: `online`, or coming back to the tab. */
  autoRetries: boolean
  /** Sending would replace words the server has that this draft never saw. */
  wouldReplace: boolean
  /** The textarea `key`: changes on a new date, and once when a draft is restored. */
  fieldKey: string
  /** Call SYNCHRONOUSLY on every keystroke, before the debounce and before the request. */
  noteKeystroke: (text: string) => void
  /** Call with the text the server just confirmed. */
  noteSaved: (savedText: string) => void
}

export function useJournalDraft({
  date,
  serverText,
  readFailed = false,
  settled,
  busy,
  send,
}: {
  date: ISODate
  /** What the server holds for this day, or undefined while that is unknown. */
  serverText: string | undefined
  /** The read failed: the server's words are unknown, so nothing is sent unattended. */
  readFailed?: boolean
  /** The read has settled — success or failure. Deciding before it lands restores over nothing. */
  settled: boolean
  /** A save is in flight, or a debounce is about to fire. The resend stands down. */
  busy: boolean
  /** Send these words now. Only ever called by the `online` / visible resend. */
  send: (text: string) => void
}): JournalDraftState {
  const [restored, setRestored] = useState<RestoredDraft | null>(null)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const [kept, setKept] = useState(true)
  /** The last text the SERVER confirmed for this day — stamped into every draft written. */
  const confirmed = useRef('')
  /** `${date}|ok` or `${date}|error`: the read state the decision below was made against. */
  const decidedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!settled) return
    const key = `${date}|${readFailed ? 'error' : 'ok'}`
    if (decidedFor.current === key) return
    const sameDay = decidedFor.current?.startsWith(`${date}|`) ?? false
    decidedFor.current = key

    if (!sameDay) setRestored(null)
    // A failed read knows nothing about the server's words, and says so by leaving the last
    // value alone rather than stamping '' into the drafts it is about to write.
    if (!readFailed) confirmed.current = serverText ?? ''

    const draft = readJournalDraft(journalDraftStorage(), date)
    setUnconfirmed(draft !== null)
    // A failed read makes the server's text UNKNOWN, so any draft is worth putting back.
    const put = readFailed ? draft : draftToRestore(draft, serverText)
    if (!put) return
    // `?? current` and never a replacement: this state feeds the textarea's key, and a key that
    // changed twice would remount the field and take the owner's undo stack with it.
    setRestored((current) => current ?? { text: put.text, typedAt: put.typedAt })
  }, [settled, readFailed, serverText, date])

  const wouldReplace =
    restored !== null &&
    unconfirmed &&
    draftWouldReplace(readJournalDraft(journalDraftStorage(), date), serverText)

  const autoRetries = unconfirmed && !readFailed && !wouldReplace

  /**
   * RETRY THAT IS NOT "AS YOU TYPE" (JRN-1b). Two triggers, both meaning the phone might be able
   * to reach the server again: the radio coming back, and the app being looked at again. No
   * interval — one composer write is not worth a polling loop.
   *
   * Assigned during render so it closes over this render's `send` and `busy`; the listeners
   * below therefore never tear down and rebuild mid-sentence.
   */
  const resendRef = useRef<() => void>(() => {})
  resendRef.current = () => {
    if (!autoRetries || busy) return
    const draft = readJournalDraft(journalDraftStorage(), date)
    if (!draft) return
    // Nothing new in it: typed, then deleted back to what is already saved.
    if (draft.text === draft.lastConfirmedText) return
    send(draft.text)
  }

  useEffect(() => {
    const resend = () => resendRef.current()
    const onVisible = () => {
      if (document.visibilityState === 'visible') resend()
    }
    window.addEventListener('online', resend)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', resend)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return {
    restored,
    unconfirmed,
    kept,
    autoRetries,
    wouldReplace,
    fieldKey: restored ? `${date}·${restored.typedAt}` : date,

    noteKeystroke: (text: string) => {
      // DURABLE FIRST. `typedAt` is the REAL wall clock and not the app's fixed demo clock on
      // purpose: it stamps when the device held the words, which is a fact about the device
      // rather than about the day being written.
      setKept(
        writeJournalDraft(journalDraftStorage(), {
          date,
          text,
          typedAt: new Date().toISOString(),
          lastConfirmedText: confirmed.current,
        }),
      )
      setUnconfirmed(true)
    },

    noteSaved: (savedText: string) => {
      confirmed.current = savedText
      // The draft was cleared by the MUTATION if the server confirmed exactly those words
      // (lib/data/index.ts). This reads the store back rather than trusting a flag.
      setUnconfirmed(readJournalDraft(journalDraftStorage(), date) !== null)
    },
  }
}

/**
 * The one sentence a composer is allowed to show when words are not saved, and it promises only
 * what is wired: the retry line appears exactly when the resend above would fire, and the device
 * is credited with keeping the words only when it actually did.
 *
 * `where` is the device in the reader's own terms — "this phone" on the mobile app, "this
 * browser" on the desktop, because a desktop user told he has something on his phone would go
 * looking for it there.
 */
export function unsavedSentence(kept: boolean, autoRetries: boolean, where: string): string {
  if (!kept) return 'Not saved — still here, retries as you type'
  return autoRetries ? `Not saved — kept ${where}, retries online` : `Not saved — kept ${where}`
}
